import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, User, Sparkles } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (!u.consent_given) nav("/onboarding");
      else nav("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="min-h-screen grid place-items-center px-4"
      data-testid="login-page"
      style={{ background: "var(--tm-bg-2)" }}
    >
      <div className="w-full max-w-md">
        <div className="tm-card mb-4">
          <div className="flex items-center gap-2 mb-2 text-[color:var(--tm-ink-3)]">
            <User size={18} />
            <span className="text-sm font-bold uppercase tracking-wider">Grown-up sign in</span>
          </div>
          <h1 className="text-3xl font-black mb-2" style={{ fontFamily: "Nunito, sans-serif" }}>
            Welcome back
          </h1>
          <p className="text-[color:var(--tm-ink-3)] mb-6">Sign in to your parent or teacher account.</p>
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="tm-label">Email</label>
              <input
                id="email"
                type="email"
                required
                className="tm-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label htmlFor="password" className="tm-label">Password</label>
              <input
                id="password"
                type="password"
                required
                minLength={6}
                className="tm-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>
            {err && (
              <p className="text-sm font-medium" style={{ color: "var(--tm-error)" }} data-testid="login-error">
                {err}
              </p>
            )}
            <button
              type="submit"
              className="tm-btn tm-btn-primary w-full"
              disabled={busy}
              data-testid="login-submit-btn"
            >
              {busy ? <Loader2 size={18} className="animate-spin" /> : null}
              Sign in
            </button>
          </form>
          <p className="mt-6 text-sm text-center text-[color:var(--tm-ink-3)]">
            Don't have an account?{" "}
            <Link to="/register" className="font-bold" style={{ color: "var(--tm-brand)" }} data-testid="login-register-link">
              Create one
            </Link>
          </p>
        </div>

        <Link
          to="/kid"
          className="tm-card block hover:border-[color:var(--tm-brand)] transition-colors"
          data-testid="login-kid-link"
        >
          <div className="flex items-center gap-3">
            <div
              className="p-3 rounded-xl"
              style={{ background: "var(--tm-bg-3)", color: "var(--tm-brand)" }}
            >
              <Sparkles size={22} />
            </div>
            <div className="flex-1">
              <p className="text-lg font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>
                Are you a kid?
              </p>
              <p className="text-[color:var(--tm-ink-3)]">Sign in with your picture PIN.</p>
            </div>
          </div>
        </Link>
      </div>
    </main>
  );
}
